// COLLECTION label — printed at the pack desk instead of a courier label
// for a collection order (delivery_type = collection: a staff purchase
// picked up at the warehouse counter, never shipped).
//
// One per box. Big "COLLECTION" header so it can never be mistaken for a
// courier label, the order number as a Code128 barcode so the counter can
// scan it, the customer's name, "Box n of N", the packed date and the
// warehouse. No address (it isn't going anywhere), no carrier.
//
// Rendered into dispatch-close-order's hidden print area and printed through
// buildPrintDocument (helpers/printAgent.ts), which inlines the .dpl-collection-*
// styles — keep the two in step. Same 4in × 6in portrait stock as the demo
// shipping label (.dispatch-print-label).
import Barcode from "react-barcode";
import { useTranslation } from "../../i18n";

type Props = {
    orderNumber: string;
    customerName: string;
    boxIndex: number;
    boxCount: number;
    warehouse?: string;
    /** Defaults to now; injectable so tests and reprints are deterministic. */
    packedAt?: Date;
};

const CollectionLabel = ({ orderNumber, customerName, boxIndex, boxCount, warehouse, packedAt }: Props) => {
    const { t } = useTranslation();
    const when = packedAt ?? new Date();
    const dateText = when.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
    return (
        <div className="dispatch-print-label dispatch-print-label--collection" data-testid="collection-label">
            <div className="dpl-collection-banner">{t("collection_label_heading")}</div>

            <div className="dpl-collection-order">
                <span className="dpl-collection-label">{t("order_title_label")}</span>
                <span className="dpl-collection-order-number">{orderNumber}</span>
            </div>

            <div className="dpl-collection-barcode">
                <Barcode value={orderNumber || "-"} height={70} width={2} fontSize={14} margin={0} displayValue={true} />
            </div>

            <div className="dpl-collection-customer">
                <span className="dpl-collection-label">{t("collection_label_customer")}</span>
                <span className="dpl-collection-customer-name">{customerName || "—"}</span>
            </div>

            <div className="dpl-collection-info">
                <div className="dpl-collection-cell">
                    <span className="dpl-collection-label">{t("box_label")}</span>
                    <span className="dpl-collection-value">{boxIndex} / {boxCount}</span>
                </div>
                <div className="dpl-collection-cell">
                    <span className="dpl-collection-label">{t("collection_label_packed")}</span>
                    <span className="dpl-collection-value">{dateText}</span>
                </div>
                {warehouse && (
                    <div className="dpl-collection-cell">
                        <span className="dpl-collection-label">{t("warehouse")}</span>
                        <span className="dpl-collection-value">{warehouse}</span>
                    </div>
                )}
            </div>

            <div className="dpl-collection-footer">{t("collection_label_heading")}</div>
        </div>
    );
};

export default CollectionLabel;
